import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
} from '../utils/compass'
import { formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

export namespace Consumer {
  export namespace AbevCosts {
    const CONTEXT_ID = 'equity:consumer:abev_costs'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'cash_cogs' | 'cash_cogs_yoy' | 'cash_cogs_yoy_base_100'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          ...(config.chart === 'cash_cogs_yoy'
            ? { convertToPercentages: Array(data.data.length).fill(true) }
            : {}),
          tooltip: { xDateFormat: '%B %Y' },
        })(config, formatSeriesNames(data)),
    }
  }

  export namespace Sidra {
    const CONTEXT_ID = 'equity:consumer:sidra'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ipca' | 'ipca_12m'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            convertToPercentages: Array(data.data.length).fill(true),
            tooltip: { xDateFormat: '%B %Y' },
          },
          { type: Array(data.data.length).fill('spline') }
        )(config, data),
    }
  }

  export namespace Sgs {
    const CONTEXT_ID = 'equity:consumer:sgs'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ibcbr'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            tooltip: { xDateFormat: '%B %Y' },
          },
          { type: Array(data.data.length).fill('spline') }
        )(config, data),
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:consumer:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'pe_ntco3_bz' | 'pe_abev3_bz' | 'pe_mdia3_bz' | 'pe_hype3_bz'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        })(config, data),
    }
  }
}
